import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Tag,
  Tabs,
} from '@arco-design/web-react';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import { errorMessage } from '../../../common/api/httpClient';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';
import { personalApi } from '../api/personalApi';
import {
  PASSWORD_MIN_LENGTH,
  passwordLengthValid,
  type PortalPersonalProfile,
} from '../model/personalModels';

type PasswordValues = {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
};

function ReadonlyField(props: { label: string; value: string }) {
  return <div className="ui-description-panel__field">
    <span className="ui-description-panel__label">{props.label}</span>
    <span className="ui-description-panel__value readonly-field readonly-field--wide">
      <span className="readonly-field__value">{props.value}</span>
    </span>
  </div>;
}

function TenantStatusField(props: { value: string }) {
  return <div className="ui-description-panel__field portal-personal-config__tenant-status">
    <span className="ui-description-panel__label">租户状态</span>
    <Tag color={statusTagColor(props.value)}>{props.value}</Tag>
  </div>;
}

function statusTagColor(value: string): 'green' | 'orange' | 'red' | 'gray' {
  switch (value) {
    case '正常':
    case '启用':
      return 'green';
    case '锁定':
      return 'orange';
    case '停用':
    case '已过期':
      return 'red';
    default:
      return 'gray';
  }
}

export function PortalPersonalView(props: {
  profile: PortalPersonalProfile;
  onPasswordChanged: () => void;
}) {
  const { message } = usePortalOverlay();
  const [form] = Form.useForm<PasswordValues>();
  const [submitting, setSubmitting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordConfigured = props.profile.passwordConfigured;
  const actionName = passwordConfigured ? '修改密码' : '初始化密码';

  const submitPassword = async () => {
    try {
      const values = await form.validate();
      setSubmitting(true);
      await personalApi.changePassword({
        currentPassword: passwordConfigured ? values.currentPassword : undefined,
        newPassword: values.newPassword,
      });
      message.success(`${actionName}成功，请重新登录`);
      form.resetFields();
      props.onPasswordChanged();
    } catch (cause) {
      if (cause instanceof Error) message.error(errorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="portal-personal-config reference-form-page">
    <Tabs className="portal-personal-config__tabs" defaultActiveTab="profile">
      <Tabs.TabPane key="profile" title={<span className="portal-personal-config__tab-title"><IconUser />个人信息</span>}>
        <div className="portal-personal-config__tab-content">
          <section className="ui-description-panel ui-description-panel--page ui-description-panel--readonly">
            <div className="ui-description-panel__head">
              <div><strong>个人信息</strong><span className="portal-personal-config__secondary">查看当前账号的租户、组织和个人归属</span></div>
              <Tag color="arcoblue">只读信息</Tag>
            </div>
            <div className="ui-description-panel__body">
              <div className="ui-description-panel__section">
                <div className="ui-description-panel__section-title">租户信息</div>
                <div className="ui-description-panel__fields">
                  <ReadonlyField label="租户名称" value={props.profile.tenantName} />
                  <TenantStatusField value={props.profile.tenantStatus} />
                </div>
              </div>
              <div className="ui-description-panel__section">
                <div className="ui-description-panel__section-title">组织信息</div>
                <div className="ui-description-panel__fields">
                  <ReadonlyField label="当前组织" value={props.profile.organizationName} />
                  <ReadonlyField label="所属部门" value={props.profile.departmentName} />
                  <ReadonlyField label="岗位 / 主要角色" value={props.profile.roleName} />
                </div>
              </div>
              <div className="ui-description-panel__section">
                <div className="ui-description-panel__section-title">用户个人信息</div>
                <div className="ui-description-panel__fields">
                  <ReadonlyField label="姓名" value={props.profile.displayName} />
                  <ReadonlyField label="手机号" value={props.profile.mobile} />
                  <ReadonlyField label="邮箱" value={props.profile.email} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </Tabs.TabPane>

      <Tabs.TabPane key="password" title={<span className="portal-personal-config__tab-title"><IconLock />密码设置</span>}>
        <div className="portal-personal-config__tab-content">
          <section className="ui-description-panel ui-description-panel--page ui-description-panel--editable portal-password-panel">
            <div className="ui-description-panel__head">
              <div><strong>{actionName}</strong><span className="portal-personal-config__secondary">
                {passwordConfigured ? '已设置登录密码，修改时需验证当前密码' : '当前账号尚未设置登录密码，无需输入当前密码；初始化后可使用账号密码登录'}
              </span></div>
            </div>
            <div className="ui-description-panel__body">
              <Form form={form} layout="vertical" className="portal-password-form">
                {passwordConfigured ? <Form.Item label="当前密码" field="currentPassword"
                  rules={[{ required: true, message: '请输入当前密码' }, { maxLength: 128 }]}>
                  <Input.Password value={currentPassword} placeholder="请输入当前密码"
                    maxLength={128} onChange={setCurrentPassword} />
                </Form.Item> : null}
                <Form.Item label="新密码" field="newPassword" rules={[
                  { required: true, message: '请输入新密码' },
                  { minLength: PASSWORD_MIN_LENGTH, message: '新密码至少需要 6 位' },
                  { maxLength: 128 },
                  ...(passwordConfigured ? [{
                    validator: (value: string | undefined, callback: (error?: string) => void) =>
                      callback(value && value === currentPassword ? '新密码不能与当前密码相同' : undefined),
                  }] : []),
                ]}>
                  <Input.Password value={newPassword} placeholder="请输入新密码"
                    maxLength={128} onChange={setNewPassword} />
                </Form.Item>
                <Form.Item label="确认新密码" field="confirmPassword" rules={[
                  { required: true, message: '请再次输入新密码' },
                  { maxLength: 128 },
                  {
                    validator: (value: string | undefined, callback: (error?: string) => void) =>
                      callback(value && value !== newPassword ? '两次输入的新密码不一致' : undefined),
                  },
                ]}>
                  <Input.Password value={confirmPassword} placeholder="请再次输入新密码"
                    maxLength={128} onChange={setConfirmPassword} />
                </Form.Item>
              </Form>
              <div className="portal-password-rules">
                <strong>密码要求</strong>
                <span className={passwordLengthValid(newPassword) ? 'passed' : ''}>6–128 位字符</span>
                {passwordConfigured ? <span className={Boolean(newPassword) && newPassword !== currentPassword ? 'passed' : ''}>不能与当前密码相同</span> : null}
                <span className={Boolean(confirmPassword) && newPassword === confirmPassword ? 'passed' : ''}>两次输入保持一致</span>
              </div>
            </div>
          </section>
          <div className="ui-form-footer">
            <div className="ui-form-footer__summary"><span className="portal-personal-config__secondary">完成后将退出当前会话，请使用新密码重新登录。</span></div>
            <div className="ui-form-footer__actions"><Button type="primary" loading={submitting}
              onClick={() => void submitPassword()}>确认{actionName}</Button></div>
          </div>
        </div>
      </Tabs.TabPane>
    </Tabs>
  </div>;
}
